import { injectable } from 'inversify';
import groupBy from 'lodash/groupBy';

import { AssistChipColor } from '@vk-hr-tek/ui/ColoredAssistChip/ColoredAssistChip.types';

import { Group, RoleModelDomain } from '@app/gen/settings';
import {
  GetAccountWidgetsExceptionsResponse,
  Exception,
} from '@app/gen/dashboard';
import { AccountGroupWithCount, UsersAccountGroups } from '@app/gen/account';

import {
  AccountDashboardExceptionsResponse,
  AccountUsersGroupsResponse,
  CompanyGroup,
} from '../../types';
import { RoleModelNameEnum } from '../../constants';

const DASHBOARD_EXCEPTION_NAME: Record<Exception['name'], string> = {
  widget_content: 'Содержание виджетов',
  widget_layout: 'Расположение виджетов',
};

@injectable()
export class SettingsAccountMapper {
  private processRoleModelName(domain: RoleModelDomain) {
    return `${domain.name}${
      domain.group_name ? ` - ${domain.group_name}` : ''
    }`;
  }

  private getGroupName(group: Group | undefined, groups: Group[]): string {
    if (!group) return '';

    if (group.parent_id) {
      const parent = groups.find(({ id }) => id === group.parent_id);

      return `${group.name} / ${this.getGroupName(parent, groups)}`;
    }

    return group.name;
  }

  processGroups(groups: Group[]) {
    return groups.map((group) => ({
      label: this.getGroupName(group, groups),
      value: group.id,
    }));
  }

  processAccountRoleModel(domains: RoleModelDomain[], groups: Group[]) {
    const allCompanyGroupsValue = 8;

    const formattedCompanyGroups = [
      {
        label: 'Все специалисты',
        value: '00000000-0000-0000-0000-000000000000',
      },
      ...this.processGroups(groups),
    ];

    const initialValues: Record<string, string[] | number> = {};
    const roleModel = domains
      .map((domain, index) => {
        const formattedAllEmployeesValues = domain.employees_values.map(
          ({ name, value }) => ({
            value,
            label: name,
          }),
        );
        const formattedManagersValues = domain.managers_values.map(
          ({ name, value }) => ({
            value,
            label: name,
          }),
        );

        return [
          {
            id: index,
            type: RoleModelNameEnum.TitleRow,
            name: {
              text: this.processRoleModelName(domain),
              type: RoleModelNameEnum.TitleRow,
            },
            tooltip: domain.tooltip,
            employees: null,
            managers: null,
            companyGroups: null,
          },
          ...(domain.objects
            ? domain.objects
                .filter(({ id, name }) => id && name)
                .map(({ id, name, access_control: accessControl, tooltip }) => {
                  const employees = accessControl?.find(
                    ({ type }) => type === 'employees',
                  );
                  const managers = accessControl?.find(
                    ({ type }) => type === 'managers',
                  );
                  const companyGroups = accessControl?.find(
                    ({ type }) => type === 'company_groups',
                  );
                  const employeesFieldName = `${id}:employees`;
                  const managersFieldName = `${id}:managers`;
                  const companyGroupsFieldName = `${id}:company_groups`;

                  initialValues[employeesFieldName] = employees?.value || 0;
                  initialValues[managersFieldName] = managers?.value || 0;

                  if (companyGroups?.group_ids) {
                    initialValues[companyGroupsFieldName] =
                      companyGroups.group_ids;
                  } else if (companyGroups?.value === allCompanyGroupsValue) {
                    initialValues[companyGroupsFieldName] =
                      formattedCompanyGroups.map((group) => group.value);
                  } else {
                    initialValues[companyGroupsFieldName] = [];
                  }

                  return {
                    id,
                    tooltip,
                    name: {
                      text: name,
                      type: RoleModelNameEnum.Default,
                    },
                    employees: {
                      enabled: employees?.enabled || false,
                      options: formattedAllEmployeesValues,
                      fieldName: employeesFieldName,
                    },
                    managers: {
                      enabled: managers?.enabled || false,
                      options: formattedManagersValues,
                      fieldName: managersFieldName,
                    },
                    companyGroups: {
                      enabled: companyGroups?.enabled || false,
                      options: formattedCompanyGroups,
                      fieldName: companyGroupsFieldName,
                    },
                  };
                })
            : []),
        ];
      })
      .flat();

    return { roleModel, initialValues };
  }

  processAccountWidgetExceptions(
    accountWidgetExceptions: GetAccountWidgetsExceptionsResponse,
  ): AccountDashboardExceptionsResponse {
    return {
      dashboardExceptions: accountWidgetExceptions.companies.map(
        ({ company, exceptions }) => ({
          id: company.id,
          companyName: company.name,
          exceptions: exceptions.map(({ name, count }) => ({
            name: DASHBOARD_EXCEPTION_NAME[name],
            counter: count,
          })),
        }),
      ),
      total: accountWidgetExceptions.total_count,
    };
  }

  transformToTreeAccountGroup = (groups: Group[]): CompanyGroup[] => {
    const grouped = groupBy(groups, (item) => item.parent_id);

    const childrenOf = (parentId: string): CompanyGroup[] => {
      if (grouped[parentId] === undefined) {
        return [];
      }

      return grouped[parentId].map(({ id, name }) => ({
        id,
        label: name,
        children: childrenOf(id),
      }));
    };

    return childrenOf('') as CompanyGroup[];
  };

  processAccountUsersGroups(
    usersAccountGroups: UsersAccountGroups,
  ): AccountUsersGroupsResponse {
    return {
      users: usersAccountGroups.items.map(({ groups, user }) => ({
        id: user.id,
        fullName:
          `${user.last_name} ${user.first_name} ${user.middle_name}`.trim(),
        employees: user.employees.map((employee) => ({
          id: employee.id,
          name: `${employee.company.name} (${employee.personnel_number})`,
        })),
        groups: groups.map(({ id, name }) => ({
          id,
          label: name,
          color: 'gray',
        })),
      })),
      total: usersAccountGroups.total_count,
    };
  }

  processAccounGroupsWithCount(accounGroupsWithCount: AccountGroupWithCount[]) {
    if (!accounGroupsWithCount.length) return [];

    return [
      { label: 'Все', id: 'all', color: AssistChipColor.blue },
      ...accounGroupsWithCount.map(({ id, name, user_count }) => ({
        id,
        label: name,
        counter: user_count,
        color: AssistChipColor.blue,
      })),
    ];
  }
}
